import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { insightsLoadingCopy as copy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../shopifyApiFetch'
import { buildShopifyAppScopeKey, useShopifyAppContext } from '../ShopifyAppContext'
import { InsightsPolling, insightsPollingActive, insightsQueryOptions } from '../queries/insightsQueries'
import { usePageLifecycle } from './usePageLifecycle'
import { page } from '../pages/insights.page'
import type { SourceSignal } from '../page-state'

export function useInsights(shopify: ShopifyTokenSource) {
  const scope = useShopifyAppContext()
  const scopeKey = buildShopifyAppScopeKey(scope)
  const polling = useMemo(() => new InsightsPolling(), [scopeKey])
  const options = insightsQueryOptions(shopify, scope, polling)
  const query = useQuery(options)
  const client = useQueryClient()
  const queryHash = hashKey(options.queryKey)
  const subscribe = useCallback(
    (accept: (signal: SourceSignal) => void) => {
      const cache = client.getQueryCache()
      const receive = () => {
        const state = cache.get(queryHash)?.state
        if (state) accept({ ...state, isError: state.status === 'error' })
      }
      const stop = cache.subscribe((event) => {
        if (event.query.queryHash === queryHash && event.type === 'updated') receive()
      })
      receive()
      return stop
    },
    [client, queryHash],
  )
  const lifecycle = usePageLifecycle(page, scopeKey, shopify, { insights: { ...query, subscribe } })
  const reload = () => {
    polling.reset()
    lifecycle.retryFailedLoad()
    return query.refetch()
  }
  return {
    lifecycle,
    isPolling: query.data?.state === 'preparing' && !query.isError && insightsPollingActive(polling),
    data: options.enabled ? (query.data ?? null) : null,
    error: options.enabled && query.isError ? copy.error : null,
    isLoading: Boolean(options.enabled && query.isPending),
    isRefreshing: Boolean(options.enabled && query.isFetching && query.data !== undefined),
    reload,
    isDiagnosticActive: () =>
      !scope.isActiveScope || scope.isActiveScope(query.data?.diagnosticAttempt?.scopeKey ?? ''),
  }
}
